import { createFileRoute, Link } from '@tanstack/react-router'
import { BookOpen, Clock, Mail, ShieldAlert } from 'lucide-react'
import { z } from 'zod'

import { SupportForm } from '@/components/support/SupportForm'
import { Card, CardContent } from '@/components/ui/card'
import { CONTACT } from '@/lib/contact'
import { pageLinks, pageMeta } from '@/lib/seo'
import { SUPPORT_TOPICS, type SupportTopic } from '@/lib/support/support'

const TOPIC_VALUES = SUPPORT_TOPICS.map((t) => t.value) as [
  SupportTopic,
  ...SupportTopic[],
]

/**
 * `?topic=` preselects the picker so anything that used to link a `mailto:` with
 * a canned subject (error messages, emails, in-app CTAs) can point here instead
 * and still arrive on the right topic. An unknown value falls back to `other`
 * rather than erroring — a bad link should still show a usable form. Optional so
 * the bare `/support` (the URL in the sitemap and canonical) serves directly
 * instead of redirecting to `?topic=other`.
 */
const searchSchema = z.object({
  topic: z.enum(TOPIC_VALUES).optional().catch('other'),
})

export const Route = createFileRoute('/support')({
  validateSearch: searchSchema,
  head: () => ({
    meta: pageMeta({
      title: 'Support · Masklabs',
      description:
        'Get help with your Masklabs account: billing questions, technical issues, sales enquiries, and abuse reports. Send a message and we reply by email.',
      path: '/support',
    }),
    links: pageLinks('/support'),
  }),
  component: SupportPage,
})

const CHANNELS = [
  {
    icon: Clock,
    title: 'Response times',
    body: 'We answer every message by email, usually within one business day. Billing and account issues are picked up first.',
  },
  {
    icon: BookOpen,
    title: 'Check the docs first',
    body: 'Setup, authentication, rotation, and error codes are covered in the documentation, which is often faster than waiting on a reply.',
    to: '/docs',
    linkLabel: 'Read the docs',
  },
  {
    icon: Mail,
    title: 'Email instead',
    body: `If you would rather use your own mail client, write to ${CONTACT.support} and you will reach the same people.`,
    href: `mailto:${CONTACT.support}`,
    linkLabel: CONTACT.support,
  },
  {
    icon: ShieldAlert,
    title: 'Reporting abuse',
    body: `To report traffic from our network, pick the abuse topic below or write to ${CONTACT.abuse} with timestamps in UTC.`,
    href: `mailto:${CONTACT.abuse}`,
    linkLabel: CONTACT.abuse,
  },
]

function SupportPage() {
  const { topic } = Route.useSearch()

  return (
    <div className="mx-auto w-full max-w-5xl px-6 py-20">
      <header className="max-w-2xl">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
          Support
        </h1>
        <p className="mt-4 text-muted-foreground">
          Tell us what is going on and we will get back to you by email. You do
          not need an account to send a message.
        </p>
      </header>

      <div className="mt-12 grid gap-10 lg:grid-cols-[1.2fr_1fr]">
        <Card>
          <CardContent className="pt-6">
            {/* `key` on the topic so a link that changes ?topic= while the page
                is already open reseeds the picker instead of keeping the old
                selection (defaultTopic is initial state only). */}
            <SupportForm key={topic ?? 'other'} defaultTopic={topic} />
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6">
          {CHANNELS.map((channel) => {
            const Icon = channel.icon
            return (
              <div key={channel.title} className="flex gap-4">
                <Icon
                  className="mt-0.5 size-5 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <div className="flex flex-col gap-1">
                  <h2 className="text-sm font-medium">{channel.title}</h2>
                  <p className="text-sm text-muted-foreground">
                    {channel.body}
                  </p>
                  {channel.to ? (
                    <Link
                      to={channel.to}
                      className="text-sm underline underline-offset-4"
                    >
                      {channel.linkLabel}
                    </Link>
                  ) : null}
                  {channel.href ? (
                    <a
                      href={channel.href}
                      className="text-sm underline underline-offset-4"
                    >
                      {channel.linkLabel}
                    </a>
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
